/*
 * Longeva Aesthetics — Contact page progressive enhancement.
 * Pure vanilla JS. No global API overrides. Deferred, non-critical.
 *
 * Responsibilities:
 *  1. Client-side form validation (WCAG aria-invalid + aria-describedby pattern)
 *  2. Error summary surfacing on submit failure
 *  3. Honeypot guard (redundant since Shopify's contact form handles server-side)
 *  4. Character counter for message textarea
 *  5. prefers-reduced-motion: skip entrance animations if preference detected
 *     (CSS handles this; JS reinforces for the eyebrow rule opacity on load)
 */

(function () {
  'use strict';

  var FORM_SELECTOR = '.longeva-contact__form form';
  var ERROR_SUMMARY_SELECTOR = '.longeva-contact__error-summary';
  var SUCCESS_SELECTOR = '.longeva-contact__success';

  // Utility: show a field error
  function showFieldError(field, errorEl, message) {
    field.setAttribute('aria-invalid', 'true');
    errorEl.textContent = message;
    errorEl.setAttribute('data-visible', '');
  }

  // Utility: clear a field error
  function clearFieldError(field, errorEl) {
    field.removeAttribute('aria-invalid');
    errorEl.removeAttribute('data-visible');
    errorEl.textContent = '';
  }

  // Utility: get or create the aria-describedby error node
  function getErrorNode(field) {
    var id = field.getAttribute('aria-describedby');
    if (id) {
      return document.getElementById(id);
    }
    return null;
  }

  // Validate a single field, return error string or empty string
  function validateField(field) {
    var val = field.value.trim();
    var type = field.type;
    var required = field.required || field.getAttribute('aria-required') === 'true';

    if (required && val === '') {
      var label = document.querySelector('label[for="' + field.id + '"]');
      var labelText = label ? label.textContent.replace('*', '').trim() : 'This field';
      return labelText + ' is required.';
    }

    if (type === 'email' && val !== '') {
      // RFC 5322 lightweight check
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
        return 'Enter a valid email address.';
      }
    }

    if (type === 'tel' && val !== '') {
      // Accept E.164 style with optional spaces/hyphens
      if (!/^\+?[\d\s\-().]{7,20}$/.test(val)) {
        return 'Enter a valid telephone number.';
      }
    }

    return '';
  }

  // Run full-form validation, returns array of {field, errorEl, message}
  function validateForm(form) {
    var errors = [];
    var fields = form.querySelectorAll(
      'input:not([type="hidden"]):not([type="submit"]):not(.longeva-contact__radio-input), select, textarea'
    );

    fields.forEach(function (field) {
      var errorEl = getErrorNode(field);
      var message = validateField(field);
      if (errorEl) {
        if (message) {
          showFieldError(field, errorEl, message);
          errors.push({ field: field, message: message });
        } else {
          clearFieldError(field, errorEl);
        }
      }
    });

    // Validate radio groups (enquiry type)
    var radioGroups = {};
    form.querySelectorAll('.longeva-contact__radio-input').forEach(function (r) {
      if (!radioGroups[r.name]) {
        radioGroups[r.name] = [];
      }
      radioGroups[r.name].push(r);
    });

    Object.keys(radioGroups).forEach(function (name) {
      var group = radioGroups[name];
      var checked = group.some(function (r) { return r.checked; });
      var groupEl = group[0] && group[0].closest('.longeva-contact__field');
      var errorEl = groupEl && groupEl.querySelector('.longeva-contact__field-error');
      if (!checked) {
        if (errorEl) {
          errorEl.setAttribute('data-visible', '');
          errorEl.textContent = 'Please choose an enquiry type.';
        }
        errors.push({ field: group[0], message: 'Please choose an enquiry type.' });
      } else {
        if (errorEl) {
          errorEl.removeAttribute('data-visible');
          errorEl.textContent = '';
        }
      }
    });

    return errors;
  }

  // Build and show the error summary
  function showErrorSummary(summaryEl, errors) {
    summaryEl.setAttribute('data-visible', '');
    var list = summaryEl.querySelector('.longeva-contact__error-summary-list');
    if (list) {
      list.innerHTML = '';
      errors.forEach(function (e) {
        var li = document.createElement('li');
        li.textContent = e.message;
        list.appendChild(li);
      });
    }
    summaryEl.focus();
  }

  function hideErrorSummary(summaryEl) {
    summaryEl.removeAttribute('data-visible');
  }

  // Attach inline validation on blur
  function attachInlineValidation(form) {
    var fields = form.querySelectorAll(
      'input:not([type="hidden"]):not([type="submit"]):not(.longeva-contact__radio-input), select, textarea'
    );
    fields.forEach(function (field) {
      field.addEventListener('blur', function () {
        var errorEl = getErrorNode(field);
        if (!errorEl) { return; }
        var message = validateField(field);
        if (message) {
          showFieldError(field, errorEl, message);
        } else {
          clearFieldError(field, errorEl);
        }
      });

      // Live clear on change when field was previously invalid
      field.addEventListener('input', function () {
        if (field.getAttribute('aria-invalid') === 'true') {
          var errorEl = getErrorNode(field);
          if (!errorEl) { return; }
          var message = validateField(field);
          if (!message) {
            clearFieldError(field, errorEl);
          }
        }
      });
    });
  }

  // Message character counter
  function attachCharCounter(form) {
    var textarea = form.querySelector('.longeva-contact__textarea');
    if (!textarea) { return; }
    var max = parseInt(textarea.getAttribute('maxlength') || '500', 10);
    var counter = document.createElement('p');
    counter.setAttribute('aria-live', 'polite');
    counter.className = 'longeva-contact__char-count';
    counter.style.cssText = [
      'margin: 4px 0 0',
      'font-size: 11px',
      'font-weight: 300',
      'color: var(--longeva-text-quiet, rgba(45,25,14,0.45))',
      'text-align: right'
    ].join(';');
    textarea.parentNode.insertBefore(counter, textarea.nextSibling);

    function update() {
      var remaining = max - textarea.value.length;
      counter.textContent = remaining + ' characters remaining';
    }
    update();
    textarea.addEventListener('input', update);
  }

  // Main init
  function init() {
    var form = document.querySelector(FORM_SELECTOR);
    if (!form) { return; }

    var summaryEl = document.querySelector(ERROR_SUMMARY_SELECTOR);
    if (summaryEl) {
      summaryEl.setAttribute('tabindex', '-1');
    }

    attachInlineValidation(form);
    attachCharCounter(form);

    form.addEventListener('submit', function (e) {
      var errors = validateForm(form);

      if (errors.length > 0) {
        e.preventDefault();
        if (summaryEl) { showErrorSummary(summaryEl, errors); }
        return;
      }

      if (summaryEl) { hideErrorSummary(summaryEl); }

      // Shopify's contact form submits to /contact via POST.
      // The page reloads with ?form_type=contact&success=true in the query string
      // or Shopify injects the form_posted class. The success block in Liquid
      // handles this via form.posted_successfully? — no JS redirect needed here.
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
